import isEmpty from 'lodash/isEmpty';
import unescape from 'lodash/unescape';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {compose} from 'redux';
import {connect} from 'react-redux';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import IconInfo from '@spothero/icons/info';
import Modal from '@spothero/ui/modal';
import ModalContent from '@spothero/ui/modal-content';
import AnimationUtils from '@spothero/utils/animation';
import withConfig from 'config/withConfig';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {getPriceRange} from 'filters/filters-selectors';
import {getStore} from 'store/store';
import {toggleFiltersView} from '../search-actions';
import SearchControls from './SearchControls';
import SingleTermSummary from './SingleTermSummary';

const DestinationInfoModalMobile = loadable(() =>
    import('destination/DestinationInfoModalMobile')
);
const SpotListFilters = loadable(() => import('../spot-list/SpotListFilters'));

class SingleTermSearchControls extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        city: PropTypes.object.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        destinationHighPrice: PropTypes.number,
        event: PropTypes.object,
        showHeader: PropTypes.bool,
        showSearchBar: PropTypes.bool,
        isMapShowing: PropTypes.bool,
        showDestinationInfoModal: PropTypes.bool,
        areFiltersVisible: PropTypes.bool,
        onFiltersToggle: PropTypes.func.isRequired,
        onActionsToggle: PropTypes.func,
    };
    static defaultProps = {
        showHeader: true,
        showSearchBar: true,
    };
    state = {
        key: uuidV4(),
        showInfoModal: false,
        showControls: !this.props.isMapShowing,
        hasUpdatedSearch: false,
        hasUpdatedDateTime: false,
    };
    _store = getStore();

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {onActionsToggle} = this.props;
        const {hasUpdatedDateTime, hasUpdatedSearch} = this.state;
        const {
            hasUpdatedDateTime: prevHasUpdatedDateTime,
            hasUpdatedSearch: prevHasUpdatedSearch,
        } = prevState;

        if (
            onActionsToggle &&
            (hasUpdatedDateTime !== prevHasUpdatedDateTime ||
                hasUpdatedSearch !== prevHasUpdatedSearch)
        ) {
            onActionsToggle(hasUpdatedDateTime || hasUpdatedSearch);
        }
    }

    _onSuggestSelect = evt => {
        this.setState({
            hasUpdatedSearch: true,
        });
    };

    _onDateTimeChange = evt => {
        this.setState({
            hasUpdatedDateTime: true,
        });
    };

    _onToggleInfoModal = evt => {
        this.setState((prevState, props) => {
            return {
                showInfoModal: !prevState.showInfoModal,
            };
        });
    };

    _onSearchClick = ({callback}) => {
        this.setState(({hasUpdatedDateTime, hasUpdatedSearch}) => {
            return {
                // if user only updated times but not the search, hide controls otherwise searching for new address
                // will redirect the page and effectively hide the controls
                showControls: !(hasUpdatedDateTime && !hasUpdatedSearch),
                hasUpdatedDateTime: false,
                hasUpdatedSearch: false,
            };
        });

        if (callback) {
            callback();
        }
    };

    _onCancelClick = evt => {
        const newState = {
            hasUpdatedDateTime: false,
            hasUpdatedSearch: false,
        };

        if (this.props.isMapShowing) {
            newState.showControls = false;
        } else {
            newState.key = uuidV4();
        }

        this.setState(newState);
    };

    _onEditClick = evt => {
        const {isMobile, isMapShowing} = this.props;

        if (isMapShowing) {
            this.setState({
                showControls: true,
            });
        } else {
            const container = isMobile ? '.SpotListContainer' : '.list-scroll';

            AnimationUtils.scrollTo(container, 0);
        }
    };

    render() {
        const {
            isMobile,
            isMonthly,
            starts,
            ends,
            activeSearchTitle,
            city,
            destination,
            destinationHighPrice,
            event,
            showHeader,
            showSearchBar,
            isMapShowing,
            showDestinationInfoModal,
            areFiltersVisible,
            onFiltersToggle,
        } = this.props;
        const {
            key,
            showInfoModal,
            showControls,
            hasUpdatedDateTime,
            hasUpdatedSearch,
        } = this.state;
        const hasDestination = !isEmpty(destination);
        const destinationTitle = hasDestination
            ? destination.title
            : city
            ? city.display_name
            : false;
        const searchTitle = destinationTitle
            ? unescape(destinationTitle)
            : null;
        const searchTitleSuffix = `${isMonthly ? 'Monthly ' : ''}Parking`;
        const areActionsVisible =
            isMapShowing || hasUpdatedDateTime || hasUpdatedSearch;
        const classes = classNames('SingleTermSearchControls', {
            'SingleTermSearchControls-with-actions': areActionsVisible,
        });

        return (
            <div className={classes}>
                {(!isMapShowing || (isMapShowing && showControls)) && (
                    <>
                        {showHeader && (
                            <div
                                className="SingleTermSearchControls-header"
                                onClick={this._onToggleInfoModal}
                            >
                                <span itemType="http://schema.org/Place">
                                    <h1>
                                        {searchTitle} {searchTitleSuffix}{' '}
                                        {showDestinationInfoModal && (
                                            <IconInfo />
                                        )}
                                    </h1>
                                    <meta
                                        itemProp="name"
                                        content={searchTitle}
                                    />
                                </span>
                            </div>
                        )}
                        <SearchControls
                            key={key}
                            className="SearchControls-polish-experiment"
                            onSearchClick={this._onSearchClick}
                            onSuggestSelect={this._onSuggestSelect}
                            onDateTimeChange={this._onDateTimeChange}
                            placeholder={
                                isMonthly
                                    ? 'City, Address or Place'
                                    : 'Address, Place, City or Venue'
                            }
                            searchButtonText="Update Search"
                            showTerm={false}
                            showActions={areActionsVisible}
                            showCancel={areActionsVisible}
                            datetimeOnly={!showSearchBar}
                            isSearchPickersExperiment
                            onCancelClick={this._onCancelClick}
                        />
                    </>
                )}
                {(!isMapShowing || (isMapShowing && !showControls)) && (
                    <SingleTermSummary
                        starts={starts}
                        ends={ends}
                        isMonthly={isMonthly}
                        title={activeSearchTitle}
                        onEditClick={this._onEditClick}
                    />
                )}
                {showDestinationInfoModal && showInfoModal && (
                    <DestinationInfoModalMobile
                        title={
                            hasDestination
                                ? isMonthly
                                    ? `${destination.title} Monthly`
                                    : destination.title
                                : isMonthly
                                ? `${city.display_name} Monthly`
                                : city.display_name
                        }
                        address={
                            hasDestination ? destination.street_address : null
                        }
                        city={
                            hasDestination
                                ? destination.city
                                : city.display_name
                        }
                        state={hasDestination ? destination.state : city.state}
                        description={
                            hasDestination
                                ? isMonthly && destination.monthly_description
                                    ? destination.monthly_description
                                    : destination.description
                                : isMonthly && city.monthly_description
                                ? city.monthly_description
                                : city.description
                        }
                        event={event}
                        onHidden={this._onToggleInfoModal}
                    />
                )}
                {areFiltersVisible && isMobile && (
                    <Modal
                        title="Filter Spots"
                        className="searchFiltersModal"
                        store={this._store}
                        onCloseClick={onFiltersToggle}
                    >
                        <ModalContent>
                            <SpotListFilters
                                highestPrice={destinationHighPrice}
                            />
                        </ModalContent>
                    </Modal>
                )}
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const mapStateToProps = state => {
        const {
            city: {data: city},
            destination: {data: destination},
            event: {data: event},
            search: {
                data: {
                    areFiltersVisible,
                    breadcrumbs: {
                        activeBreadcrumb: {label: activeSearchTitle},
                    },
                },
            },
            searchRequest: {starts, ends, monthly},
        } = state;
        const {priceHigh} = getPriceRange(state);

        return {
            isMonthly: monthly,
            starts,
            ends,
            activeSearchTitle,
            city,
            destination,
            event,
            areFiltersVisible,
            destinationHighPrice: priceHigh,
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = {
    onFiltersToggle: toggleFiltersView,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        makeMapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SingleTermSearchControls);
